import { useId, useMemo, useState } from 'react';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';

import { Badge, Button, Checkbox, Input, Popover } from '../../../components/ui';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { DANH_MUC, NHOM_HANH_DONG, timMa } from '../nghiep-vu/danh-muc-hanh-dong';
import type { NhomHanhDong } from '../nghiep-vu/danh-muc-hanh-dong';
import { useTenHanhDong } from './NhanSuKien';

export interface LocThaoTacProps {
  nhom: NhomHanhDong[];
  chiTiet: string[];
  onDoiNhom: (nhom: NhomHanhDong[]) => void;
  onDoiChiTiet: (chiTiet: string[]) => void;
}

/**
 * Bộ lọc thao tác hai tầng (ADR-0089 mục 6): một hàng chip nhóm — bấm một lần
 * là lọc, bấm nhiều chip là "hoặc" — và nút "Chi tiết" mở ô chọn từng thao
 * tác, có tìm kiếm, gom theo nhóm. Chip là nút bật/tắt (`aria-pressed`), tự
 * xuống dòng ở 768px.
 */
export function LocThaoTac({ nhom, chiTiet, onDoiNhom, onDoiChiTiet }: LocThaoTacProps) {
  const { t, locale } = useT();
  const tenHanhDong = useTenHanhDong();
  const [mo, setMo] = useState(false);
  const [tim, setTim] = useState('');
  const idNhom = useId();

  const batTatNhom = (n: NhomHanhDong) =>
    onDoiNhom(nhom.includes(n) ? nhom.filter((x) => x !== n) : [...nhom, n]);
  const batTatMa = (ma: string) =>
    onDoiChiTiet(chiTiet.includes(ma) ? chiTiet.filter((x) => x !== ma) : [...chiTiet, ma]);

  const theoNhom = useMemo(
    () =>
      NHOM_HANH_DONG.map((n) => ({ nhom: n, ma: timMa(DANH_MUC[n], tim, tenHanhDong) })).filter(
        (g) => g.ma.length > 0,
      ),
    [tim, tenHanhDong],
  );

  const noiDung = (
    <div className="flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-3">
      <Input
        allowClear
        autoFocus
        prefix={<SearchOutlined />}
        aria-label={t('nhatKy.loc.timThaoTac')}
        placeholder={t('nhatKy.loc.timThaoTac')}
        value={tim}
        onChange={(e) => setTim(e.target.value)}
      />
      <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
        {theoNhom.length === 0 ? (
          <p className="py-4 text-center text-sm text-content-muted">{t('nhatKy.loc.khongCoThaoTac')}</p>
        ) : (
          theoNhom.map((g) => (
            <fieldset key={g.nhom} className="space-y-1">
              <legend className="mb-1 text-xs font-semibold text-content-secondary">
                {t(`nhatKy.nhom.${g.nhom}` as KhoaDich)}
              </legend>
              {g.ma.map((ma) => (
                <Checkbox key={ma} checked={chiTiet.includes(ma)} onChange={() => batTatMa(ma)} className="flex w-full">
                  {/* Tên trên, mã dưới: mã dài không chen vào giữa tên khi xuống dòng. */}
                  <span className="flex min-w-0 flex-col">
                    <span className="text-sm text-content-primary">{tenHanhDong(ma)}</span>
                    <span className="break-all font-mono text-2xs text-content-muted">{ma}</span>
                  </span>
                </Checkbox>
              ))}
            </fieldset>
          ))
        )}
      </div>
      {chiTiet.length > 0 && (
        <div className="flex justify-end border-t border-default pt-2">
          <Button variant="ghost" size="sm" onClick={() => onDoiChiTiet([])}>
            {t('nhatKy.loc.boChonChiTiet')}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span id={idNhom} className="text-xs text-content-muted">
        {t('nhatKy.loc.nhomThaoTac')}
      </span>
      <div role="group" aria-labelledby={idNhom} className="flex flex-wrap items-center gap-2">
        {NHOM_HANH_DONG.map((n) => {
          const dangChon = nhom.includes(n);
          return (
            <button
              key={n}
              type="button"
              aria-pressed={dangChon}
              onClick={() => batTatNhom(n)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors',
                dangChon
                  ? 'border-primary-600 bg-primary-50 font-medium text-primary-700'
                  : 'border-default bg-surface text-content-secondary hover:bg-subtle',
              )}
            >
              {t(`nhatKy.nhom.${n}` as KhoaDich)}
            </button>
          );
        })}
      </div>
      <Popover
        open={mo}
        onOpenChange={setMo}
        trigger="click"
        placement="bottomLeft"
        title={t('nhatKy.loc.chiTietMo')}
        content={noiDung}
      >
        <Button variant="outline" size="sm" icon={<FilterOutlined />} aria-expanded={mo} aria-haspopup="dialog">
          {t('nhatKy.loc.chiTiet')}
          {chiTiet.length > 0 && (
            <Badge variant="primary">
              <span aria-hidden>{soVN(chiTiet.length, locale)}</span>
              <span className="sr-only">{t('nhatKy.loc.daChonChiTiet', { so: soVN(chiTiet.length, locale) })}</span>
            </Badge>
          )}
        </Button>
      </Popover>
    </div>
  );
}
